/**
 * AssetSubsystem - Asset loading for the player runtime
 *
 * In **browser** mode loads assets from HTTP via fetch.
 * In **engine player** mode uses the host-provided ResourceManager
 * (no fetch / AudioContext available).
 */

import {
  Subsystem,
  type SubsystemConfig,
  SubsystemPriority,
} from '@engine/core';
import type { GameManifest } from '../types';
import { IS_ENGINE_PLAYER, hostResource } from '../env';

// Re-export the manifest type so existing imports keep working
export type { GameManifest as AssetManifest };

export interface AssetSubsystemOptions {
  assetBase: string;
  manifest: string;
}

export class AssetSubsystem extends Subsystem {
  readonly config: SubsystemConfig = {
    name: 'assets',
    priority: SubsystemPriority.Core,
    optional: false,
  };

  private readonly options: AssetSubsystemOptions;
  private manifest: GameManifest | null = null;

  // Caches
  private readonly textures = new Map<string, HTMLImageElement>();
  private readonly audio = new Map<string, AudioBuffer>();
  private readonly json = new Map<string, unknown>();
  private readonly binary = new Map<string, ArrayBuffer>();

  // Shared audio context (created lazily on first decode)
  private audioContext: AudioContext | null = null;

  constructor(options: AssetSubsystemOptions) {
    super();
    this.options = options;
  }

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  async onInitialize(): Promise<void> {
    await this.loadManifest();
    console.log('[AssetSubsystem] Manifest loaded');
  }

  onShutdown(): void {
    this.textures.clear();
    this.audio.clear();
    this.json.clear();
    this.binary.clear();
    this.audioContext?.close().catch(() => {});
    this.audioContext = null;
    this.manifest = null;
  }

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  getManifest(): GameManifest | null {
    return this.manifest;
  }

  getTexture(id: string): HTMLImageElement | undefined {
    return this.textures.get(id);
  }

  getAudio(id: string): AudioBuffer | undefined {
    return this.audio.get(id);
  }

  getJson<T = unknown>(id: string): T | undefined {
    return this.json.get(id) as T | undefined;
  }

  getBinary(id: string): ArrayBuffer | undefined {
    return this.binary.get(id);
  }

  /**
   * Drop an asset from the CPU-side caches (decoded image / audio buffer / parsed JSON / bytes).
   * Called by the level lifecycle's ResourceManager when an asset is no longer referenced by any
   * loaded level. The GPU texture handle is freed separately via RenderSubsystem.releaseTexture.
   */
  evict(id: string): void {
    this.textures.delete(id);
    this.audio.delete(id);
    this.json.delete(id);
    this.binary.delete(id);
  }

  /** Build full URL for a path relative to the asset base */
  resolveUrl(path: string): string {
    const base = this.options.assetBase.endsWith('/')
      ? this.options.assetBase
      : this.options.assetBase + '/';
    return base + path;
  }

  // --- Loaders --------------------------------------------------------------

  /**
   * The native host ResourceManager is NOT concurrency-safe — parallel reads share an archive/stream
   * and throw `ConcurrentOperationsNotSupported` / `IndexOutOfRangeException`. The UI kicks ~18 font
   * atlas loads (+ textures) at once, so route every host read through this serial gate. No-op in the
   * browser path (fetch/Image are concurrency-safe and never call this).
   */
  private hostGate: Promise<unknown> = Promise.resolve();
  private hostRead<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.hostGate.then(fn, fn); // run after the previous read settles (ok or error)
    this.hostGate = run.then(undefined, () => undefined);
    return run;
  }

  async loadTexture(id: string, path: string): Promise<HTMLImageElement> {
    if (this.textures.has(id)) return this.textures.get(id)!;

    let img: HTMLImageElement;

    if (IS_ENGINE_PLAYER && hostResource) {
      img = await this.hostRead(() => hostResource!.getImageResourceAsync(path));
    } else {
      img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(`Failed to load texture: ${path}`));
        img.src = this.resolveUrl(path);
      });
    }

    this.textures.set(id, img);
    return img;
  }

  async loadAudio(id: string, path: string): Promise<AudioBuffer> {
    if (this.audio.has(id)) return this.audio.get(id)!;

    let arrayBuffer: ArrayBuffer;

    if (IS_ENGINE_PLAYER && hostResource) {
      arrayBuffer = await this.hostRead(() => hostResource!.getBinaryResourceAsync(path));
    } else {
      const response = await fetch(this.resolveUrl(path));
      arrayBuffer = await response.arrayBuffer();
    }

    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
    const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

    this.audio.set(id, audioBuffer);
    return audioBuffer;
  }

  async loadJson<T = unknown>(id: string, path: string): Promise<T> {
    if (this.json.has(id)) return this.json.get(id) as T;

    let data: unknown;

    if (IS_ENGINE_PLAYER && hostResource) {
      const text = await this.hostRead(() => hostResource!.getTextResourceAsync(path));
      data = JSON.parse(text);
    } else {
      const response = await fetch(this.resolveUrl(path));
      data = await response.json();
    }

    this.json.set(id, data);
    return data as T;
  }

  async loadText(id: string, path: string): Promise<string> {
    const cached = this.json.get(id);
    if (typeof cached === 'string') return cached;

    let text: string;

    if (IS_ENGINE_PLAYER && hostResource) {
      text = await this.hostRead(() => hostResource!.getTextResourceAsync(path));
    } else {
      const response = await fetch(this.resolveUrl(path));
      text = await response.text();
    }

    this.json.set(id, text);
    return text;
  }

  async loadBinary(id: string, path: string): Promise<ArrayBuffer> {
    if (this.binary.has(id)) return this.binary.get(id)!;

    let data: ArrayBuffer;

    if (IS_ENGINE_PLAYER && hostResource) {
      data = await this.hostRead(() => hostResource!.getBinaryResourceAsync(path));
    } else {
      const response = await fetch(this.resolveUrl(path));
      data = await response.arrayBuffer();
    }

    this.binary.set(id, data);
    return data;
  }

  /** Get the shared AudioContext for playback */
  getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
    return this.audioContext;
  }

  // --- Batch ----------------------------------------------------------------

  /** Preload every asset listed in the manifest */
  async preloadAll(onProgress?: (loaded: number, total: number) => void): Promise<void> {
    if (!this.manifest) throw new Error('Manifest not loaded');

    const { assets } = this.manifest;
    const items: Array<{ type: string; id: string; path: string }> = [];

    if (assets.textures) {
      for (const [id, path] of Object.entries(assets.textures)) {
        items.push({ type: 'texture', id, path });
      }
    }
    if (assets.audio) {
      for (const [id, path] of Object.entries(assets.audio)) {
        items.push({ type: 'audio', id, path });
      }
    }
    if (assets.levels) {
      for (const [id, path] of Object.entries(assets.levels)) {
        items.push({ type: 'json', id, path });
      }
    }
    if (assets.graphs) {
      for (const [id, path] of Object.entries(assets.graphs)) {
        items.push({ type: 'json', id, path });
      }
    }

    let loaded = 0;
    const total = items.length;

    for (const item of items) {
      try {
        switch (item.type) {
          case 'texture': await this.loadTexture(item.id, item.path); break;
          case 'audio': await this.loadAudio(item.id, item.path); break;
          case 'json': await this.loadJson(item.id, item.path); break;
        }
      } catch (err) {
        console.warn(`[AssetSubsystem] Failed to load ${item.type} ${item.id}:`, err);
      }
      loaded++;
      onProgress?.(loaded, total);
    }
  }

  // ==========================================================================
  // PRIVATE
  // ==========================================================================

  private async loadManifest(): Promise<void> {
    if (IS_ENGINE_PLAYER && hostResource) {
      const text = await hostResource.getTextResourceAsync(this.options.manifest);
      this.manifest = JSON.parse(text);
    } else {
      const response = await fetch(this.resolveUrl(this.options.manifest));
      this.manifest = await response.json();
    }
  }
}
